import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable } from 'rxjs';
import { ApiService, ApiResponse } from './api.service';
import {
  AccountCompanyDto,
  AccountCompanyListRequest,
  AccountCompanyLookupDto,
  AccountCompanyLookupSaveDto,
  AccountCompanyLookupSearchRequest,
  AccountCompanyIsolationValidationDto,
  AccountCompanyGstVerificationDto,
} from '../models/dtos/account-company.dto';
import { PagedListData } from '../models/paged-list.model';
import {
  ACCOUNTING_COMPANY_OPEN_SESSION_KEY,
  ACCOUNTING_COMPANY_SESSION_KEY,
  DEFAULT_ACCOUNTING_COMPANY_ID,
} from '../constants/accounting-company.constants';

@Injectable({ providedIn: 'root' })
export class AccountCompanyService {
  private readonly api = inject(ApiService);
  private readonly platformId = inject(PLATFORM_ID);

  getDefaultCompany(): Observable<ApiResponse<AccountCompanyDto>> {
    return this.api.get<AccountCompanyDto>('AccountCompany/default');
  }

  getCompanyById(accountCompanyId: string): Observable<ApiResponse<AccountCompanyDto>> {
    return this.api.get<AccountCompanyDto>(`AccountCompany/${accountCompanyId}`);
  }

  getCompanies(
    request: AccountCompanyListRequest,
  ): Observable<ApiResponse<PagedListData<AccountCompanyDto>>> {
    return this.api.post<PagedListData<AccountCompanyDto>>('AccountCompany/list', request);
  }

  searchStates(
    request?: AccountCompanyLookupSearchRequest,
  ): Observable<ApiResponse<AccountCompanyLookupDto[]>> {
    return this.api.post<AccountCompanyLookupDto[]>('AccountCompany/search-states', request ?? {});
  }

  searchCountries(
    request?: AccountCompanyLookupSearchRequest,
  ): Observable<ApiResponse<AccountCompanyLookupDto[]>> {
    return this.api.post<AccountCompanyLookupDto[]>('AccountCompany/search-countries', request ?? {});
  }

  createState(dto: AccountCompanyLookupSaveDto): Observable<ApiResponse<AccountCompanyLookupDto>> {
    return this.api.post<AccountCompanyLookupDto>('AccountCompany/states/save', dto);
  }

  createCountry(dto: AccountCompanyLookupSaveDto): Observable<ApiResponse<AccountCompanyLookupDto>> {
    return this.api.post<AccountCompanyLookupDto>('AccountCompany/countries/save', dto);
  }

  saveCompany(dto: AccountCompanyDto): Observable<ApiResponse<string>> {
    return this.api.post<string>('AccountCompany/save', dto);
  }

  verifyGst(gstNumber: string): Observable<ApiResponse<AccountCompanyGstVerificationDto>> {
    return this.api.post<AccountCompanyGstVerificationDto>('AccountCompany/verify-gst', { gstNumber });
  }

  deleteCompany(accountCompanyId: string): Observable<ApiResponse<boolean>> {
    return this.api.delete<boolean>(`AccountCompany/${accountCompanyId}`);
  }

  validateMultiCompanyIsolation(): Observable<ApiResponse<AccountCompanyIsolationValidationDto>> {
    return this.api.get<AccountCompanyIsolationValidationDto>('AccountCompany/validate-isolation');
  }

  getActiveCompanyId(): string {
    if (!isPlatformBrowser(this.platformId)) {
      return DEFAULT_ACCOUNTING_COMPANY_ID;
    }
    return (
      sessionStorage.getItem(ACCOUNTING_COMPANY_SESSION_KEY)?.trim().toLowerCase() ||
      DEFAULT_ACCOUNTING_COMPANY_ID
    );
  }

  setActiveCompanyId(accountCompanyId: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    sessionStorage.setItem(ACCOUNTING_COMPANY_SESSION_KEY, accountCompanyId.trim().toLowerCase());
  }

  markCompanyOpen(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    sessionStorage.setItem(ACCOUNTING_COMPANY_OPEN_SESSION_KEY, '1');
  }

  shutCompany(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    sessionStorage.removeItem(ACCOUNTING_COMPANY_OPEN_SESSION_KEY);
  }

  isCompanyOpen(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return sessionStorage.getItem(ACCOUNTING_COMPANY_OPEN_SESSION_KEY) === '1';
  }
}
